import { type ReactNode, useEffect, useState } from 'react'
import clsx from 'clsx'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { NavMenuCategory } from 'modules/products'
import LazyLoader from 'shared/ui/LazyLoader/LazyLoader'
import LoadingOverlay from 'shared/ui/LoadingOverlay'

import { SelectedCategoryHeader } from '../../types'

import CategoriesNavigationTree from './tree/CategoriesNavigationTree'

import styles from './CategoriesNavigationMenu.module.scss'

interface CategoriesNavigationMenuProps {
  id: string
  open: boolean
  categories: NavMenuCategory[]
  promotionsSlot?: ReactNode
  isLoading: boolean
  handleClose: () => void
  ref: React.RefObject<HTMLDivElement | null>
}

const CategoriesNavigationMenu = ({
  id,
  open,
  isLoading,
  handleClose,
  categories,
  promotionsSlot,
  ref
}: CategoriesNavigationMenuProps) => {
  const pathname = usePathname()
  const [selectedCategoryHeader, setSelectedCategoryHeader] =
    useState<SelectedCategoryHeader>({
      name: '',
      slug: '',
      productsCount: 0
    })

  // navigation is handled via Links; keep effect below to close on route change
  useEffect(() => {
    handleClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // When menu is closed, make content non-focusable so it's not in tab order
  useEffect(() => {
    const el = ref?.current

    if (!el) return

    if (open) {
      el.removeAttribute('inert')
    } else {
      el.setAttribute('inert', '')
    }
  }, [open, ref])

  return (
    <div
      id={id}
      className={clsx(styles.listWrapper, {
        [styles.listWrapperOpen]: open
      })}
      {...{ open }}
      ref={ref}
    >
      <LazyLoader style={{ height: '100%' }}>
        {isLoading ? (
          <LoadingOverlay />
        ) : (
          <>
            <div className={styles.header}>
              {selectedCategoryHeader.name && (
                <Link
                  className={styles.categoryTitleLink}
                  href={selectedCategoryHeader.slug}
                  title={`Przejdź do kategorii ${selectedCategoryHeader.name}`}
                >
                  <h2 className={styles.categoryTitle}>
                    <span data-testid='category-title'>
                      {selectedCategoryHeader.name}
                    </span>
                    <span
                      className={styles.categoryTitleProductsCount}
                      data-testid='category-title-products-count'
                    >
                      {selectedCategoryHeader.productsCount > 0 &&
                        `${selectedCategoryHeader.productsCount}`}
                    </span>
                  </h2>
                </Link>
              )}
            </div>
            <div className={styles.columnsWrapper}>
              <CategoriesNavigationTree
                categories={categories}
                handleClose={handleClose}
                setSelectedCategoryHeader={setSelectedCategoryHeader}
              />
              {promotionsSlot}
            </div>
          </>
        )}
      </LazyLoader>
      {/* thanks to this element, focus trap will work correctly because it will be the last focusable element in the menu */}
      <p
        id='categories-navigation-menu-hint'
        className='visually-hidden'
        tabIndex={0}
      >
        Aby wyjść z menu, naciśnij przycisk Esc.
      </p>
    </div>
  )
}

export default CategoriesNavigationMenu
